// services/sessionManager.js
class SessionManager {
  constructor() {
    this.LAST_TAB_FLAG = "last_tab_closed";
    this.SESSIONS_KEY = "active_tabs";
    this.AUTH_FLAG = "isAuthenticated";
    this.TOKEN_KEY = "token";
    this.channel = new BroadcastChannel("auth_session");
    this.tabId = null;
    this.initialized = false;
  }

  init() {
    // Prevent double initialization
    if (this.initialized) {
      return this.tabId;
    }

    // Generate unique tab ID
    let tabId = sessionStorage.getItem("tabId");
    const isNewTab = !tabId;

    if (!tabId) {
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("tabId", tabId);
    } else {
    }
    this.tabId = tabId;

    // Check if should logout ONLY for new tabs (not refreshes)
    if (isNewTab) {
      const shouldLogout = this.shouldLogoutOnLoad();
      if (shouldLogout) {
        this.clearAuthData();
        this.initialized = true;
        return null; // Signal that logout is needed
      }
    } else {
      // Clear the last tab flag on refresh to prevent false logout
      localStorage.removeItem(this.LAST_TAB_FLAG);
    }

    // Mark this tab as active
    this.markTabActive();

    // Setup listeners
    this.setupListeners();

    this.initialized = true;
    return this.tabId;
  }

  markTabActive() {
    const activeTabs = this.getActiveTabs();
    activeTabs[this.tabId] = Date.now();
    localStorage.setItem(this.SESSIONS_KEY, JSON.stringify(activeTabs));

    // Clear last tab flag when marking tab as active
    localStorage.removeItem(this.LAST_TAB_FLAG);
  }

  markTabInactive() {
    const activeTabs = this.getActiveTabs();
    delete activeTabs[this.tabId];
    localStorage.setItem(this.SESSIONS_KEY, JSON.stringify(activeTabs));

    const remainingTabs = Object.keys(activeTabs).length;

    // If no more active tabs and user is authenticated, set the flag
    const isAuth = localStorage.getItem(this.AUTH_FLAG) === "true";
    if (remainingTabs === 0 && isAuth) {
      localStorage.setItem(this.LAST_TAB_FLAG, "true");
    }
  }

  getActiveTabs() {
    try {
      const tabs = localStorage.getItem(this.SESSIONS_KEY);
      return tabs ? JSON.parse(tabs) : {};
    } catch (e) {
      console.error(e);
      return {};
    }
  }

  setupListeners() {
    // Prevent duplicate listeners
    if (this.listenersSetup) {
      return;
    }
    this.listenersSetup = true;

    // Listen for beforeunload to mark tab as inactive
    this.beforeUnloadHandler = () => {
      this.markTabInactive();
    };
    window.addEventListener("beforeunload", this.beforeUnloadHandler);

    // Listen for tab becoming visible again
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === "visible" && this.tabId) {
        this.markTabActive();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityChangeHandler);

    // Listen for broadcast messages
    this.channel.onmessage = (event) => {
      if (event.data === "logout") {
        this.handleLogout();
      } else if (event.data === "login") {
        this.handleLogin();
      }
    };

    // Heartbeat to keep tab alive
    this.heartbeatInterval = setInterval(() => {
      if (this.tabId) {
        this.markTabActive();
      }
    }, 5000); // Every 5 seconds
  }

  shouldLogoutOnLoad() {
    const lastTabClosed = localStorage.getItem(this.LAST_TAB_FLAG) === "true";
    const wasAuthenticated = localStorage.getItem(this.AUTH_FLAG) === "true";
    const activeTabs = this.getActiveTabs();
    const hasActiveTabs = Object.keys(activeTabs).length > 0;

    // Only logout if:
    // 1. Last tab was closed
    // 2. User was authenticated
    // 3. No active tabs exist (none survived)
    return lastTabClosed && wasAuthenticated && !hasActiveTabs;
  }

  clearAuthData() {
    // Clear authentication-related data
    const authKeys = [
      "access_token",
      "token",
      "user",
      "expireAt",
      "InactivityLimitReached",
      this.AUTH_FLAG,
    ];

    authKeys.forEach((key) => localStorage.removeItem(key));

    // Clear session storage
    sessionStorage.clear();

    // Clear last tab flag after clearing auth
    localStorage.removeItem(this.LAST_TAB_FLAG);
    localStorage.removeItem(this.SESSIONS_KEY);
  }

  broadcastLogout() {
    try {
      if (!this.channel) {
        this.channel = new BroadcastChannel("auth_session");
      }
      this.channel.postMessage("logout");
    } catch (_) {
      try {
        this.channel = new BroadcastChannel("auth_session");
        this.channel.postMessage("logout");
      } catch (__) {}
    }
  }

  broadcastLogin() {
    localStorage.removeItem(this.LAST_TAB_FLAG);
    try {
      if (!this.channel) {
        this.channel = new BroadcastChannel("auth_session");
      }
      this.channel.postMessage("login");
    } catch (_) {
      try {
        this.channel = new BroadcastChannel("auth_session");
        this.channel.postMessage("login");
      } catch (__) {}
    }
  }

  handleLogout() {
    // Dispatch event for app to handle
    window.dispatchEvent(new CustomEvent("session-logout"));
  }

  handleLogin() {
    localStorage.removeItem(this.LAST_TAB_FLAG);
  }

  setAuthenticated(isAuth) {
    if (isAuth) {
      localStorage.setItem(this.AUTH_FLAG, "true");
      this.broadcastLogin();
    } else {
      localStorage.removeItem(this.AUTH_FLAG);
    }
  }

  cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }

    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        "visibilitychange",
        this.visibilityChangeHandler
      );
    }

    this.markTabInactive();
    if (this.channel) {
      try {
        this.channel.close();
      } catch (_) {}
      this.channel = null;
    }
    this.initialized = false;
    this.listenersSetup = false;
  }
}

// Export singleton
const sessionManager = new SessionManager();
export default sessionManager;
